import { collection, doc, getDoc, getDocs, setDoc, updateDoc } from "firebase/firestore";
import { db } from "../lib/firebase";
import {
  ACTIVITY_PROFILES,
  PROFILE_CORRECTION_REASONS,
  detectSuspiciousActivityFlags,
  normalizeActivityProfile
} from "../lib/activityIntegrity";

export type GeneratedRoutePoint = {
  latitude: number;
  longitude: number;
  timestamp: number;
  speed: number;
  altitude?: number;
  altitudeMeters?: number;
  accuracy?: number;
  pointOfInterestType?: string;
  pointOfInterestName?: string;
  pointOfInterestSymbol?: string;
  pointOfInterestDescription?: string;
  pointOfInterestSourceType?: string;
};

export type UserRideSummary = {
  id: string;
  title: string;
  date: number;
  distanceMeters: number;
  durationSeconds: number;
  averageSpeedKmh: number;
  maxSpeedKmh: number;
  elevationGainMeters: number;
  elevationLossMeters: number;
  minAltitudeMeters?: number;
  maxAltitudeMeters?: number;
  recordingProfileKey?: string;
  excludedFromRecords: boolean;
  excludedFromStats: boolean;
  suspiciousFlags: string[];
  profileCorrectionReason?: string;
  isDeleted: boolean;
  isFromOtherRider: boolean;
  photoUrl?: string;
  routePoints: GeneratedRoutePoint[];
  controlPoints: GeneratedRoutePoint[];
  sourceRouteFileName?: string;
  sourceRouteFileType?: string;
  syncedAt?: number;
  updatedAt?: number;
};

export type UserRideDetail = UserRideSummary;

export type GeneratedRouteDraft = {
  userId: string;
  title: string;
  routePoints: GeneratedRoutePoint[];
  controlPoints?: GeneratedRoutePoint[];
  distanceMeters: number;
  durationSeconds: number;
  averageSpeedKmh: number;
  maxSpeedKmh?: number;
  elevationGainMeters?: number;
  elevationLossMeters?: number;
  minAltitudeMeters?: number;
  maxAltitudeMeters?: number;
  recordingProfileKey?: string;
  sourceRouteFileName?: string;
  sourceRouteFileType?: string;
};

function stringValue(value: unknown, fallback = "") {
  return typeof value === "string" ? value : fallback;
}

function optionalStringValue(value: unknown) {
  return typeof value === "string" && value.trim() ? value : undefined;
}

function numberValue(value: unknown, fallback = 0) {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function optionalNumberValue(value: unknown) {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function booleanValue(value: unknown, fallback = false) {
  return typeof value === "boolean" ? value : fallback;
}

function stringListValue(value: unknown) {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === "string" && item.trim().length > 0);
  }
  if (typeof value === "string" && value.trim()) {
    return value.split("|").map((item) => item.trim()).filter(Boolean);
  }
  return [];
}

function millisValue(value: unknown) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (value instanceof Date) return value.getTime();
  if (value && typeof value === "object") {
    const candidate = value as { seconds?: number; toMillis?: () => number };
    if (typeof candidate.toMillis === "function") return candidate.toMillis();
    if (typeof candidate.seconds === "number") return candidate.seconds * 1000;
  }
  return 0;
}

function pointNumberValue(data: Record<string, unknown>, primaryKey: string, legacyKey: string, fallback = 0) {
  return numberValue(data[primaryKey] ?? data[legacyKey], fallback);
}

function pointStringValue(data: Record<string, unknown>, primaryKey: string, legacyKey: string) {
  return optionalStringValue(data[primaryKey] ?? data[legacyKey]);
}

function normalizeRoutePoint(value: unknown): GeneratedRoutePoint | null {
  if (!value || typeof value !== "object") return null;
  const data = value as Record<string, unknown>;
  const latitude = pointNumberValue(data, "latitude", "a", Number.NaN);
  const longitude = pointNumberValue(data, "longitude", "b", Number.NaN);

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) return null;
  if (latitude === 0 && longitude === 0) return null;

  const point: GeneratedRoutePoint = {
    latitude,
    longitude,
    timestamp: pointNumberValue(data, "timestamp", "c", Date.now()),
    speed: pointNumberValue(data, "speed", "d", 0)
  };

  const altitude = optionalNumberValue(data.altitude ?? data.altitudeMeters ?? data.e);
  const accuracy = optionalNumberValue(data.accuracy ?? data.f);
  const pointOfInterestType = pointStringValue(data, "pointOfInterestType", "g");
  const pointOfInterestName = pointStringValue(data, "pointOfInterestName", "h");
  const pointOfInterestSymbol = pointStringValue(data, "pointOfInterestSymbol", "i");
  const pointOfInterestDescription = pointStringValue(data, "pointOfInterestDescription", "j");
  const pointOfInterestSourceType = pointStringValue(data, "pointOfInterestSourceType", "k");

  if (altitude !== undefined) {
    point.altitude = altitude;
    point.altitudeMeters = altitude;
  }
  if (accuracy !== undefined) point.accuracy = accuracy;
  if (pointOfInterestType) point.pointOfInterestType = pointOfInterestType;
  if (pointOfInterestName) point.pointOfInterestName = pointOfInterestName;
  if (pointOfInterestSymbol) point.pointOfInterestSymbol = pointOfInterestSymbol;
  if (pointOfInterestDescription) point.pointOfInterestDescription = pointOfInterestDescription;
  if (pointOfInterestSourceType) point.pointOfInterestSourceType = pointOfInterestSourceType;

  return point;
}

async function tryDecompressGzipBase64(value: string) {
  const decompressionStream = (
    globalThis as typeof globalThis & {
      DecompressionStream?: new (format: string) => TransformStream<Uint8Array, Uint8Array>;
    }
  ).DecompressionStream;

  if (!decompressionStream) throw new Error("gzip-decompression-unavailable");

  const normalized = value.replace(/\s/g, "");
  const binary = window.atob(normalized);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  const stream = new Blob([bytes]).stream().pipeThrough(new decompressionStream("gzip"));
  return new Response(stream).text();
}

async function parseCompressedRoutePoints(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return [];

  const json = trimmed.startsWith("[") || trimmed.startsWith("{")
    ? trimmed
    : await tryDecompressGzipBase64(trimmed);
  const parsed = JSON.parse(json);
  return Array.isArray(parsed) ? parsed : [];
}

async function routePointsFromData(data: Record<string, unknown>) {
  if (data.isCompressed === true && typeof data.routePointsCompressed === "string") {
    try {
      const rawPoints = await parseCompressedRoutePoints(data.routePointsCompressed);
      return rawPoints.map(normalizeRoutePoint).filter((point): point is GeneratedRoutePoint => point !== null);
    } catch {
      return [];
    }
  }

  return Array.isArray(data.routePoints)
    ? data.routePoints.map(normalizeRoutePoint).filter((point): point is GeneratedRoutePoint => point !== null)
    : [];
}

function controlPointsFromData(data: Record<string, unknown>) {
  return Array.isArray(data.controlPoints)
    ? data.controlPoints.map(normalizeRoutePoint).filter((point): point is GeneratedRoutePoint => point !== null)
    : [];
}

async function rideFromData(id: string, data: Record<string, unknown>): Promise<UserRideDetail> {
  const date = millisValue(data.date) || Number.parseInt(id, 10) || 0;

  const ride = {
    id,
    title: stringValue(data.title, "Διαδρομή"),
    date,
    distanceMeters: numberValue(data.distance),
    durationSeconds: numberValue(data.duration),
    averageSpeedKmh: numberValue(data.averageSpeed),
    maxSpeedKmh: numberValue(data.maxSpeed),
    elevationGainMeters: numberValue(data.elevationGain),
    elevationLossMeters: numberValue(data.elevationLoss),
    minAltitudeMeters: optionalNumberValue(data.minAltitude),
    maxAltitudeMeters: optionalNumberValue(data.maxAltitude),
    recordingProfileKey: optionalStringValue(data.recordingProfileKey),
    excludedFromRecords: booleanValue(data.excludedFromRecords),
    excludedFromStats: booleanValue(data.excludedFromStats),
    suspiciousFlags: stringListValue(data.suspiciousFlags),
    profileCorrectionReason: optionalStringValue(data.profileCorrectionReason),
    isDeleted: booleanValue(data.isDeleted),
    isFromOtherRider: booleanValue(data.isFromOtherRider),
    photoUrl: optionalStringValue(data.photoUrl),
    routePoints: await routePointsFromData(data),
    controlPoints: controlPointsFromData(data),
    sourceRouteFileName: optionalStringValue(data.sourceRouteFileName),
    sourceRouteFileType: optionalStringValue(data.sourceRouteFileType),
    syncedAt: millisValue(data.syncedAt) || undefined,
    updatedAt: millisValue(data.updatedAt) || undefined
  };
  return {
    ...ride,
    suspiciousFlags: ride.suspiciousFlags.length > 0 ? ride.suspiciousFlags : detectSuspiciousActivityFlags(ride)
  };
}

function rideDocumentData(draft: GeneratedRouteDraft, date: number, existing?: Record<string, unknown>) {
  const now = Date.now();
  const cleanTitle = draft.title.trim();
  const existingCorrectionReason = optionalStringValue(existing?.profileCorrectionReason);
  const existingSuspiciousFlags = stringListValue(existing?.suspiciousFlags);
  const isFromOtherRider = booleanValue(existing?.isFromOtherRider);
  const excludedFromRecords = booleanValue(existing?.excludedFromRecords);
  const excludedFromStats = booleanValue(existing?.excludedFromStats);
  const recordingProfileKey = draft.recordingProfileKey ?? optionalStringValue(existing?.recordingProfileKey);
  const correctionAlreadyConfirmed =
    existingCorrectionReason === PROFILE_CORRECTION_REASONS.confirmedAsIs ||
    existingCorrectionReason === PROFILE_CORRECTION_REASONS.profileChanged;
  const activityCandidate: UserRideSummary = {
    id: String(date),
    title: cleanTitle,
    date,
    distanceMeters: draft.distanceMeters,
    durationSeconds: draft.durationSeconds,
    averageSpeedKmh: draft.averageSpeedKmh,
    maxSpeedKmh: draft.maxSpeedKmh ?? 0,
    elevationGainMeters: draft.elevationGainMeters ?? 0,
    elevationLossMeters: draft.elevationLossMeters ?? 0,
    minAltitudeMeters: draft.minAltitudeMeters,
    maxAltitudeMeters: draft.maxAltitudeMeters,
    recordingProfileKey,
    excludedFromRecords,
    excludedFromStats,
    suspiciousFlags: existingSuspiciousFlags,
    profileCorrectionReason: existingCorrectionReason,
    isDeleted: false,
    isFromOtherRider,
    routePoints: draft.routePoints,
    controlPoints: draft.controlPoints ?? [],
    sourceRouteFileName: draft.sourceRouteFileName,
    sourceRouteFileType: draft.sourceRouteFileType,
    syncedAt: now,
    updatedAt: now
  };
  const suspiciousFlags = existingSuspiciousFlags.length > 0 || correctionAlreadyConfirmed
    ? existingSuspiciousFlags
    : detectSuspiciousActivityFlags(activityCandidate);

  return {
    userId: draft.userId,
    title: cleanTitle,
    distance: draft.distanceMeters,
    duration: draft.durationSeconds,
    averageSpeed: draft.averageSpeedKmh,
    maxSpeed: draft.maxSpeedKmh ?? 0,
    date,
    routePoints: draft.routePoints,
    controlPoints: draft.controlPoints ?? [],
    isCompressed: false,
    elevationGain: draft.elevationGainMeters ?? 0,
    elevationLoss: draft.elevationLossMeters ?? 0,
    minAltitude: draft.minAltitudeMeters ?? null,
    maxAltitude: draft.maxAltitudeMeters ?? null,
    isDeleted: false,
    isFromOtherRider,
    excludedFromRecords,
    excludedFromStats,
    suspiciousFlags,
    profileCorrectionReason: existingCorrectionReason,
    syncedAt: now,
    updatedAt: now,
    ...(recordingProfileKey ? { recordingProfileKey } : {}),
    ...(draft.sourceRouteFileName ? { sourceRouteFileName: draft.sourceRouteFileName } : {}),
    ...(draft.sourceRouteFileType ? { sourceRouteFileType: draft.sourceRouteFileType } : {})
  };
}

function validateGeneratedRouteDraft(draft: GeneratedRouteDraft) {
  const cleanTitle = draft.title.trim();
  if (!draft.userId) throw new Error("auth-required");
  if (!cleanTitle) throw new Error("title-required");
  if (draft.routePoints.length < 2) throw new Error("route-points-required");
}

export async function loadUserRides(userId: string) {
  if (!userId) return [];

  const snapshot = await getDocs(collection(db, "users", userId, "rides"));
  const rides = await Promise.all(snapshot.docs.map((doc) => rideFromData(doc.id, doc.data())));

  return rides
    .filter((ride) => !ride.isDeleted)
    .sort((a, b) => b.date - a.date);
}

export async function loadUserRideDetail(userId: string, rideId: string) {
  if (!userId || !rideId) return null;

  const snapshot = await getDoc(doc(db, "users", userId, "rides", rideId));
  if (!snapshot.exists()) return null;

  const ride = await rideFromData(snapshot.id, snapshot.data());
  return ride.isDeleted ? null : ride;
}

export async function saveGeneratedUserRoute(draft: GeneratedRouteDraft) {
  validateGeneratedRouteDraft(draft);

  const now = Date.now();
  await setDoc(doc(db, "users", draft.userId, "rides", now.toString()), rideDocumentData(draft, now));
  return now;
}

export async function updateGeneratedUserRoute(rideId: string, draft: GeneratedRouteDraft, originalDate?: number) {
  validateGeneratedRouteDraft(draft);
  if (!rideId) throw new Error("ride-id-required");

  const rideRef = doc(db, "users", draft.userId, "rides", rideId);
  const existingSnapshot = await getDoc(rideRef);
  if (!existingSnapshot.exists()) throw new Error("ride-not-found");

  const existing = existingSnapshot.data();
  const date = originalDate || millisValue(existing.date) || Number.parseInt(rideId, 10) || Date.now();
  await setDoc(rideRef, rideDocumentData(draft, date, existing));
  return rideId;
}

export async function updateUserRideActivityProfile(userId: string, rideId: string, profileKey: string) {
  const normalizedProfileKey = normalizeActivityProfile(profileKey);
  if (!userId) throw new Error("auth-required");
  if (!rideId) throw new Error("ride-id-required");
  if (!normalizedProfileKey || !ACTIVITY_PROFILES.includes(normalizedProfileKey)) {
    throw new Error("invalid-profile");
  }

  const ride = await loadUserRideDetail(userId, rideId);
  if (!ride) throw new Error("ride-not-found");
  const updatedRide = {
    ...ride,
    profileCorrectionReason: PROFILE_CORRECTION_REASONS.profileChanged,
    recordingProfileKey: normalizedProfileKey
  };

  await updateDoc(doc(db, "users", userId, "rides", rideId), {
    recordingProfileKey: normalizedProfileKey,
    suspiciousFlags: detectSuspiciousActivityFlags(updatedRide),
    profileCorrectionReason: PROFILE_CORRECTION_REASONS.profileChanged,
    updatedAt: Date.now()
  });
}

export async function updateUserRideRecordExclusion(userId: string, rideId: string, excluded: boolean) {
  if (!userId) throw new Error("auth-required");
  if (!rideId) throw new Error("ride-id-required");
  await updateDoc(doc(db, "users", userId, "rides", rideId), {
    excludedFromRecords: excluded,
    excludedFromStats: excluded,
    profileCorrectionReason: excluded
      ? PROFILE_CORRECTION_REASONS.excludedFromRecords
      : PROFILE_CORRECTION_REASONS.includedInRecords,
    updatedAt: Date.now()
  });
}

export async function confirmUserRideActivityProfile(userId: string, rideId: string) {
  if (!userId) throw new Error("auth-required");
  if (!rideId) throw new Error("ride-id-required");
  await updateDoc(doc(db, "users", userId, "rides", rideId), {
    profileCorrectionReason: PROFILE_CORRECTION_REASONS.confirmedAsIs,
    updatedAt: Date.now()
  });
}
